import { Link } from "react-router-dom";
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import SoftBox from "../common/softBox";
import SoftTypography from "../common/softTypography";

interface BreadcrumbsProps {
  icon?: React.ReactNode;
  title: string;
  route: string | string[];
  light?: boolean;
}

const SEGMENT_LABELS: Record<string, string> = {
  role: "Roles",
  roles: "Roles",
  user: "Users",
  users: "Users",
  staff: "Staffs",
  staffs: "Staffs",
  edit: "Edit",
  create: "Create",
  "expiry-return-configuration": "Expiry Return Configuration",
};

const toTitleCase = (value: string) => {
  if (SEGMENT_LABELS[value]) return SEGMENT_LABELS[value];
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isObjectId = (value: string) => /^[a-f\d]{24}$/i.test(value);

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ icon, title, route, light = false }) => {
  // When the last segment is a record id, drop it and use the previous segment as the title
  // so that e.g. /role/edit/123 renders "Home / Roles / Edit".
  const segments = Array.isArray(route) ? [...route] : [];
  if (segments.length > 0 && isObjectId(segments[segments.length - 1])) {
    segments.pop();
  }
  const routes = segments.slice(0, -1);
  const lastSegment = segments[segments.length - 1] || title;
  const lastLabel = toTitleCase(lastSegment);

  return (
    <SoftBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { primary, grey } }) => (light ? primary.main : grey[600]),
          },
        }}
      >
        <Link to="/">
          <SoftTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            {icon && typeof icon !== "string" ? icon : "Home"}
          </SoftTypography>
        </Link>

        {routes.map((el) => (
          <Link to={`/${el}`} key={el}>
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {toTitleCase(el)}
            </SoftTypography>
          </Link>
        ))}

        <SoftTypography
          variant="button"
          fontWeight="regular"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {lastLabel}
        </SoftTypography>
      </MuiBreadcrumbs>

      <SoftTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {lastLabel}
      </SoftTypography>
    </SoftBox>
  );
};

export default Breadcrumbs;
